/**
 * Password recovery by emailed code (JOSH-73) — error classification.
 *
 * One mapping from Supabase Auth errors to the Portuguese messages shown by
 * both Ops and web, for the three recovery calls:
 *   1. `resetPasswordForEmail(email)`              → classifyRecoveryRequest
 *   2. `verifyOtp({ email, token, type: 'recovery' })` → classifyRecoveryVerify
 *   3. `updateUser({ password })`                  → classifyRecoveryUpdate
 *
 * Classification goes by `code` / `status` / error `name` (supabase-js
 * `AuthApiError`, `AuthRetryableFetchError`, `AuthWeakPasswordError`,
 * `AuthSessionMissingError`). The English message is only read where Auth
 * gives no distinguishing code: the per-user 60 s frequency limit and the
 * project-wide email limit share `over_email_send_rate_limit`.
 *
 * The request step never reveals whether an account exists: every outcome
 * except "no connection" and the project-wide email limit maps to the same
 * neutral message (the per-user limit only fires for real accounts).
 */

import { passwordErrorMessage } from './password';

// ==================== MESSAGES ====================

export const RECOVERY_MESSAGES = {
  /** Request accepted (or any outcome that must not reveal the account). */
  requestSent:
    'Se existir uma conta com este e-mail, enviamos um código para ele. Confira sua caixa de entrada.',
  /** Project-wide email limit: independent of the typed email. */
  requestRateLimited: 'Muitos pedidos agora. Aguarde um momento e tente de novo.',
  /** No network / Auth unreachable. */
  offline: 'Sem conexão com o servidor. Verifique sua internet e tente de novo.',
  /** Wrong or expired code; Auth cannot tell the two apart. */
  invalidCode: 'Código inválido ou expirado',
  /** Per-IP verify limit. */
  verifyRateLimited: 'Muitas tentativas. Aguarde alguns minutos e tente de novo.',
  /** Unexpected verify failure. */
  verifyFailed: 'Não foi possível confirmar o código. Tente de novo.',
  /** Unexpected failure saving the new password. */
  updateFailed: 'Não foi possível salvar a nova senha. Tente de novo.',
  /**
   * Banned or deactivated user. Deliberately generic: it must not reveal the
   * account state.
   */
  recoveryFailed:
    'Não foi possível concluir a recuperação. Procure o administrador da sua empresa.',
  /** Recovery abandoned after verify (back, cancel, idle, cold start, lost session). */
  abandoned: 'O código já foi usado. Peça um novo código para continuar.',
  /** Password saved, but `signOut({ scope: 'others' })` failed. Non-blocking. */
  sessionsNotEnded:
    'Senha alterada. Não conseguimos encerrar suas outras sessões agora; elas expiram em até 1 hora.',
} as const;

/** Inactive user after verify: same text as a banned user (R19). */
export const RECOVERY_INACTIVE_USER_MESSAGE = RECOVERY_MESSAGES.recoveryFailed;
export const RECOVERY_ABANDONED_MESSAGE = RECOVERY_MESSAGES.abandoned;
export const RECOVERY_SESSIONS_NOT_ENDED_MESSAGE = RECOVERY_MESSAGES.sessionsNotEnded;
export const RECOVERY_INVALID_CODE_MESSAGE = RECOVERY_MESSAGES.invalidCode;

// ==================== ERROR SHAPE ====================

interface MaybeAuthError {
  name?: string;
  code?: string;
  status?: number;
  message?: string;
}

function asAuthError(err: unknown): MaybeAuthError | null {
  if (!err || typeof err !== 'object') return null;
  return err as MaybeAuthError;
}

/**
 * Network failure or Auth unreachable. supabase-js throws
 * `AuthRetryableFetchError` with status 0 when fetch itself fails, and with
 * the gateway status for 502/503/504/52x. A raw fetch `TypeError` (web
 * "Failed to fetch", React Native "Network request failed") is covered too.
 */
export function isRecoveryNetworkError(err: unknown): boolean {
  const e = asAuthError(err);
  if (!e) return false;
  if (e.name === 'AuthRetryableFetchError') return true;
  if (e.status === 0) return true;
  const msg = (e.message ?? '').toLowerCase();
  return (
    e.name === 'TypeError' &&
    /failed to fetch|network request failed|networkerror|load failed/.test(msg)
  );
}

/** GoTrue's per-user frequency text: "For security purposes, you can only request this after N seconds." */
const PER_USER_FREQUENCY_RE = /only request this after \d+ seconds?/i;

// ==================== REQUEST ====================

export type RecoveryRequestOutcome = 'sent' | 'rate_limited' | 'offline';

export interface RecoveryRequestResult {
  outcome: RecoveryRequestOutcome;
  message: string;
}

/**
 * Classify the result of `resetPasswordForEmail`. Pass the `error` field
 * (null/undefined on success).
 *
 * - success, unknown email, SMTP failure (500), per-user 60 s 429 → `sent` (neutral)
 * - project-wide 429 (`over_email_send_rate_limit` without the per-user
 *   frequency text, or any other 429) → `rate_limited`
 * - network / status 0 / gateway → `offline`
 */
export function classifyRecoveryRequest(err: unknown): RecoveryRequestResult {
  const e = asAuthError(err);
  if (!e) return { outcome: 'sent', message: RECOVERY_MESSAGES.requestSent };

  if (isRecoveryNetworkError(e)) {
    return { outcome: 'offline', message: RECOVERY_MESSAGES.offline };
  }

  const isRateLimit = e.status === 429 || e.code === 'over_email_send_rate_limit';
  if (isRateLimit) {
    // The per-user limit only fires for a real account; answering it
    // differently would reveal that the account exists.
    if (PER_USER_FREQUENCY_RE.test(e.message ?? '')) {
      return { outcome: 'sent', message: RECOVERY_MESSAGES.requestSent };
    }
    return { outcome: 'rate_limited', message: RECOVERY_MESSAGES.requestRateLimited };
  }

  return { outcome: 'sent', message: RECOVERY_MESSAGES.requestSent };
}

// ==================== VERIFY ====================

export type RecoveryVerifyOutcome =
  | 'invalid_code'
  | 'rate_limited'
  | 'blocked'
  | 'offline'
  | 'failed';

export interface RecoveryVerifyResult {
  outcome: RecoveryVerifyOutcome;
  message: string;
}

/**
 * Classify a failed `verifyOtp({ type: 'recovery' })`.
 *
 * - `otp_expired` (wrong or expired; Auth answers both the same), or a code
 *   rejected by validation → `invalid_code`
 * - 429 / `over_request_rate_limit` → `rate_limited`
 * - `user_banned` (deactivated user, U14) → `blocked`, generic recovery failure
 * - network → `offline`
 * - anything else → `failed`
 */
export function classifyRecoveryVerify(err: unknown): RecoveryVerifyResult {
  const e = asAuthError(err);
  if (!e) return { outcome: 'failed', message: RECOVERY_MESSAGES.verifyFailed };

  if (isRecoveryNetworkError(e)) {
    return { outcome: 'offline', message: RECOVERY_MESSAGES.offline };
  }
  if (e.code === 'user_banned') {
    return { outcome: 'blocked', message: RECOVERY_MESSAGES.recoveryFailed };
  }
  if (e.status === 429 || e.code === 'over_request_rate_limit') {
    return { outcome: 'rate_limited', message: RECOVERY_MESSAGES.verifyRateLimited };
  }
  if (
    e.code === 'otp_expired' ||
    e.code === 'validation_failed' ||
    // Older Auth versions answer without a code.
    (!e.code && /token has expired or is invalid/i.test(e.message ?? ''))
  ) {
    return { outcome: 'invalid_code', message: RECOVERY_MESSAGES.invalidCode };
  }
  return { outcome: 'failed', message: RECOVERY_MESSAGES.verifyFailed };
}

// ==================== UPDATE ====================

export type RecoveryUpdateOutcome =
  | 'password_rejected'
  | 'session_lost'
  | 'blocked'
  | 'offline'
  | 'failed';

export interface RecoveryUpdateResult {
  outcome: RecoveryUpdateOutcome;
  message: string;
}

/**
 * Classify a failed `updateUser({ password })` during recovery.
 *
 * - `weak_password` (policy or HIBP) and `same_password` → `password_rejected`,
 *   worded by `passwordErrorMessage`; the user stays on the step
 * - recovery session gone (`AuthSessionMissingError`, `session_not_found`,
 *   `session_expired`) → `session_lost`, abandonment message
 * - `user_banned` → `blocked`, generic recovery failure
 * - network → `offline`
 * - anything else → `failed`
 */
export function classifyRecoveryUpdate(err: unknown): RecoveryUpdateResult {
  const e = asAuthError(err);
  if (!e) return { outcome: 'failed', message: RECOVERY_MESSAGES.updateFailed };

  if (isRecoveryNetworkError(e)) {
    return { outcome: 'offline', message: RECOVERY_MESSAGES.offline };
  }

  const passwordMessage = passwordErrorMessage(e);
  if (passwordMessage) {
    return { outcome: 'password_rejected', message: passwordMessage };
  }

  if (e.code === 'user_banned') {
    return { outcome: 'blocked', message: RECOVERY_MESSAGES.recoveryFailed };
  }
  if (
    e.name === 'AuthSessionMissingError' ||
    e.code === 'session_not_found' ||
    e.code === 'session_expired'
  ) {
    return { outcome: 'session_lost', message: RECOVERY_MESSAGES.abandoned };
  }
  return { outcome: 'failed', message: RECOVERY_MESSAGES.updateFailed };
}
